import { useEffect, useState } from "react";
import { useGetTargets } from "../../../helpers/queries/targets/useGetTargets";
import {
  cn,
  convertTargetToArray,
  formatFullDate,
  formatTime,
} from "../../../lib/utils";
import AddTargetModal from "./modals/AddTargetModal";
import { Spinner } from "../../../components/Spinner";
import RunScanModal from "./modals/RunScanModal";
import { FiCompass, FiEdit, FiGlobe, FiTrash2 } from "react-icons/fi";
import { Button } from "../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTarget } from "../../../helpers/api";
import DeleteModal from "../../../components/modals/DeleteModal";
import { toast } from "react-toastify";
import empty from "../../../assets/images/empty_assets.png";
import ScanAssetModal from "../../../components/modals/ScanAssetModal";
import { usePaginationSync } from "../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../components/Pagination";
import EditTargetModal from "./modals/EditTargetModal";
import { SquareArrowOutUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useGetUserRole } from "../../../hooks/useGetUserRole";
// import AllTargets from "./components/AllTargets";
import DataTable from "../../../components/DataTable";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

const Targets = () => {
  const [isScanned, setIScanned] = useState("");
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } = useGetTargets(
    currentPage,
    isScanned
  );
  const [targetData, setTargetData] = useState({});
  const [openEdit, setOpenEdit] = useState(false);
  const [openScan, setOpenScan] = useState(false);

  const { role } = useGetUserRole();

  useEffect(() => {
    setLastPage(data?.data?.last_page);
  }, [data]);

  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { key: "all", label: "All" },
    { key: "new", label: "New" },
    { key: "scanned", label: "Scanned" },
  ];

  useEffect(() => {
    if (activeTab === "all") {
      setIScanned("");
    } else if (activeTab === "new") {
      setIScanned(false);
    } else {
      setIScanned(true);
    }
  }, [activeTab]);

  const [showExtra, setShowExtra] = useState(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleDeleteModal = () => setOpenDeleteModal(null);

  const queryClient = useQueryClient();
  const { mutate: deleteMutation, isPending } = useMutation({
    mutationFn: (id) => deleteTarget(id),
    onSuccess: () => {
      toast("Asset deleted successfully");
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["targets"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete asset");
    },
  });

  const handleDelete = (id) => deleteMutation(id);

  const columns = [
    {
      accessorKey: "name",
      header: () => <span>Name</span>,
      cell: ({ row }) => (
        <span className="break-all truncate overflow-hidden text-nowrap">
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: "target",
      header: () => <span>Target</span>,
      disableTruncate: true,
      cell: ({ row }) => {
        const parts = row.original.target.split(",").map((part) => part.trim());
        const rowId = row.id; // 👈 use row.id (stable in react-table)

        return (
          <div className="flex-1 relative flex-row flex gap-2 text-left break-all mr-2">
            <div
              className={cn(
                parts.length > 1 ? "max-w-[110px]" : "max-w-[140px]",
                "w-fit px-2 py-1 rounded-full bg-neutral200"
              )}
            >
              <p className="truncate">{parts[0]}</p>
            </div>
            {parts.length > 1 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="w-fit px-[10px] py-1 rounded-full bg-neutral200 cursor-pointer"
                    onMouseEnter={() => setShowExtra(rowId)} // 👈 use rowId
                    onMouseLeave={() => setShowExtra(null)}
                  >
                    +{parts.length - 1}
                  </span>
                </TooltipTrigger>
                <TooltipContent side={"bottom"} align={"center"}>
                  {showExtra === rowId && ( // 👈 compare with rowId
                    <div>
                      {parts.slice(1, 4).join(", ")}
                      {parts.length > 4 && " ..."}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
    },

    {
      accessorKey: "description",
      width: "15%",
      header: () => <span>Description</span>,
      cell: ({ row }) => (
        <span className="truncate overflow-hidden text-nowrap">
          {row.original.description}
        </span>
      ),
    },
    {
      accessorKey: "scan_count",
      width: "15%",
      header: () => <span className="block text-center">No. of Scans</span>,
      cell: ({ row }) =>
        row.original.scan_count && row.original.scan_count > 0 ? (
          <a
            href={`/dashboard/scan-history/${btoa(row.original.id.toString())}`}
            className="text-blue-600 hover:underline block text-center"
          >
            {row.original.scan_count}
          </a>
        ) : (
          <span className="block text-center">0</span>
        ),
    },
    {
      accessorKey: "created_at",
      header: () => <span>Created On</span>,
      cell: ({ row }) => (
        <span>
          <span className="text-neutral800">
            {formatFullDate(row.original.created_at)},
          </span>{" "}
          <span className="text-neutral600">
            {formatTime(row.original.created_at)}
          </span>
        </span>
      ),
    },
    {
      accessorKey: "last_scanned",
      header: () => <span>Last Scanned</span>,
      cell: ({ row }) =>
        row.original.last_scanned ? (
          <span>
            <span className="text-neutral800">
              {formatFullDate(row.original.last_scanned)},
            </span>{" "}
            <span className="text-neutral600">
              {formatTime(row.original.last_scanned)}
            </span>
          </span>
        ) : (
          <span>-</span>
        ),
    },
    {
      id: "action",
      header: () => (
        <span
          className={cn(
            role && role !== "Viewer" ? "block text-center" : "hidden"
          )}
        >
          Action
        </span>
      ),
      width: role && role !== "Viewer" ? "25%" : "10%",
      cell: ({ row }) => {
        const item = row.original;
        const targetList = convertTargetToArray(item?.target);

        return (
          <TooltipProvider>
            <div
              className={cn(
                role && role === "Viewer" ? "justify-start" : "justify-end",
                "flex items-center space-x-2"
              )}
            >
              {targetList?.length > 1 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to={`/dashboard/assets/${item?.id}`} state={{ targetData: item }}>
                      <Button
                        className="w-fit bg-blue-100 hover:bg-blue-200 text-neutral1000"
                        variant="ghost"
                      >
                        <SquareArrowOutUpRight size={16} />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>View more</TooltipContent>
                </Tooltip>
              )}

              {role && role !== "Viewer" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                        variant="ghost"
                        onClick={() => {
                          setTargetData(item);
                          setOpenScan(true);
                        }}
                      >
                        <FiCompass size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Scan target</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                        variant="ghost"
                        onClick={() => {
                          setTargetData(item);
                          setOpenEdit(true);
                        }}
                      >
                        <FiEdit size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit target</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                        variant="ghost"
                        onClick={() => {
                          setSelectedItem(item);
                          setOpenDeleteModal(true);
                        }}
                      >
                        <FiTrash2 size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete target</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        );
      },
    },
  ];

  const filteredColumns = columns.filter((col) => {
    if (
      activeTab === "new" &&
      (col.accessorKey === "last_scanned" || col.accessorKey === "scan_count")
    ) {
      return false;
    }
    return true;
  });

  return (
    <>
      <div className="w-full">
        <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b mb-4">
          <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
            <div className="flex gap-3 items-center">
              <div>
                <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                  <FiGlobe className="text-neutral700 text-[20px]" />
                </div>
              </div>
              <div>
                <p className="text-darkGrey font-semibold md:block hidden">
                  Targets
                </p>
                <p className="text-neutral600 md:text-base text-sm">
                  Add and monitor the websites or IP addresses you want to keep
                  secure.
                </p>
              </div>
            </div>
            {role && role !== "Viewer" && <AddTargetModal />}
            {/* <AddTargetModal /> */}
          </div>
        </div>

        <div className="w-[250px] rounded-lg bg-neutral200 h-[40px] flex py-1 relative overflow-hidden ml-4">
          <div
            className={`absolute top-1 left-1  h-[calc(100%-8px)] rounded-lg bg-white transition-all duration-500`}
            style={{
              width: `${97.5 / tabs.length}%`,
              transform: `translateX(${
                tabs.findIndex((tab) => tab.key === activeTab) * 100
              }%)`,
            }}
          ></div>

          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`flex-1 relative z-10 text-center font-medium transition-colors duration-300 cursor-pointer ${
                activeTab === tab.key ? "text-neutral900" : "text-neutral700"
              }`}
              // onMouseEnter={() => {
              //   setActiveTab(tab.key);
              // }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && data?.data?.data?.length > 0 && (
          <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
            <DataTable columns={filteredColumns} data={data?.data?.data} />

            <PaginationComponent
              lastPage={data?.data?.last_page}
              currentPage={currentPage}
              handlePageChange={updatePage}
            />
          </div>
        )}

        {data?.data?.data?.length === 0 &&
          !isLoading &&
          isSuccess &&
          (activeTab === "scanned" ? (
            <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
              <img src={empty} alt="empty" />
              <p className=" font-semibold my-2 text-darkGrey">
                No assets have been scanned yet
              </p>
              <p className="text-neutral600 text-sm max-w-[360px] mb-4">
                You’ll see assets here once a scan has been completed. To get
                started, run a scan on any of your added assets.
              </p>
              {role &&
                role !== "Viewer" &&
                (data?.data?.data?.length === 0 ? (
                  <AddTargetModal />
                ) : (
                  <ScanAssetModal />
                ))}
            </div>
          ) : (
            <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
              <img src={empty} alt="empty" />
              <p className=" font-semibold my-2 text-darkGrey">
                No assets added yet
              </p>
              <p className="text-neutral600 text-sm max-w-[360px] mb-4">
                Start by adding your first asset. This can be domains or IP
                addresses you'd like to monitor for vulnerabilities.
              </p>
              {role && role !== "Viewer" && <AddTargetModal />}
            </div>
          ))}
      </div>

      <EditTargetModal
        data={targetData}
        openEdit={openEdit}
        setOpenEdit={setOpenEdit}
        assetList={true}
      />

      <RunScanModal
        data={targetData}
        openScan={openScan}
        setOpenScan={setOpenScan}
        assetList={false}
      />

      <DeleteModal
        onOpenChange={setOpenDeleteModal}
        open={openDeleteModal}
        data={selectedItem?.name}
        id={selectedItem?.id}
        title="Delete asset"
        handleDelete={handleDelete}
        loading={isPending}
      />
    </>
  );
};

export default Targets;
